import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUCTS_STATUS_QUERY_KEY } from '@/apiHooks/queryKeys';
import { getProducts } from '@/api/products/getProducts';
import type { Product } from '@/@types/product';
import { ProductType } from '@/@types/product';

const POLL_INTERVAL = 2000;
const POLL_MAX_TIME = 30000;

export const useRefetchUntilProduct = (onConfirmed?: () => void) => {
  const queryClient = useQueryClient();
  const [polling, setPolling] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const startTime = useRef(0);
  const confirmedRef = useRef(false);
  const productTypeRef = useRef<ProductType>(ProductType.WohnenPlusLight);

  useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
    enabled: polling,
    refetchInterval: (data) => {
      if (Date.now() - startTime.current > POLL_MAX_TIME) {
        setPolling(false);
        return false;
      }
      const found = (data as Product[] | undefined)?.some((p) => p.productType === productTypeRef.current);
      if (found && !confirmedRef.current) {
        confirmedRef.current = true;
        queryClient.refetchQueries({ queryKey: ['entitlements'] });
        queryClient.refetchQueries({ queryKey: PRODUCTS_STATUS_QUERY_KEY });
        setPolling(false);
        setConfirmed(true);
        onConfirmed?.();
        return false;
      }
      return POLL_INTERVAL;
    },
  });

  const startPolling = useCallback((productType: ProductType) => {
    productTypeRef.current = productType;
    startTime.current = Date.now();
    confirmedRef.current = false;
    setConfirmed(false);
    setPolling(true);
  }, []);

  return { startPolling, isPolling: polling, isConfirmed: confirmed };
};
